import styled from "styled-components";

const blue = "#1597e5";
const ink = "#17272f";

export const HeroStage = styled.figure`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(300px, 0.72fr) minmax(500px, 1.28fr);
  gap: clamp(28px, 5vw, 72px);
  align-items: center;
  width: 100%;
  margin: 0;
  padding: clamp(52px, 7vw, 96px) max(6vw, 48px);
  color: ${ink};
  background: linear-gradient(145deg, #eaf8ff 0%, #f7fbff 58%, #fff6ef 100%);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 48px 24px 34px;
  }
`;

export const HeroCopy = styled.div`
  max-width: 470px;
`;

export const Eyebrow = styled.div`
  margin-bottom: 16px;
  color: #087bc1;
  font-size: 12px;
  line-height: 18px;
  font-weight: 650;
  letter-spacing: 0.08em;
`;

export const HeroHeading = styled.h2`
  margin: 0 0 20px;
  color: ${ink};
  font-size: clamp(40px, 5vw, 70px);
  line-height: 1.02;
  font-weight: 560;
  letter-spacing: -0.04em;
`;

export const HeroBody = styled.p`
  max-width: 440px;
  margin: 0;
  color: #486581;
  font-size: 18px;
  line-height: 29px;
`;

export const HeroImage = styled.img`
  display: block;
  width: min(100%, 780px);
  margin: 0 auto;
  object-fit: contain;
`;

export const Question = styled.p`
  margin: 0;
  padding-left: 24px;
  border-left: 3px solid ${blue};
  color: var(--project-emphasis);
  font-size: 24px;
  line-height: 34px;
  font-weight: 520;
  text-align: left;
`;

export const ResearchExplorer = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(54px, 7vw, 88px) max(6vw, 48px);
  color: #ffffff;
  background: #111719;
`;

export const ResearchInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const ExplorerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 30px;
  align-items: flex-end;
  margin-bottom: 38px;

  h3 {
    max-width: 690px;
    margin: 0;
    font-size: clamp(31px, 4vw, 50px);
    line-height: 1.08;
    font-weight: 520;
    letter-spacing: -0.025em;
  }

  p {
    margin: 0 0 5px;
    color: #aebcc3;
    font-size: 13px;
    line-height: 20px;
    font-style: italic;
    white-space: nowrap;
  }

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 28px;

    p {
      white-space: normal;
    }
  }
`;

export const ExplorerGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(170px, 0.8fr) 44px minmax(220px, 1.15fr) 44px minmax(220px, 1.15fr);
  gap: 0;
  align-items: stretch;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const ExplorerColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const ExplorerLabel = styled.span`
  display: block;
  margin-bottom: 12px;
  color: #7f929c;
  font-size: 11px;
  line-height: 17px;
  font-weight: 650;
  letter-spacing: 0.09em;
`;

export const FindingOptions = styled.div`
  display: grid;
  gap: 8px;
  height: 100%;
`;

export const FindingOption = styled.button`
  box-sizing: border-box;
  width: 100%;
  padding: 17px 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9px;
  color: rgba(255, 255, 255, 0.34);
  background: rgba(255, 255, 255, 0.025);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  transition: color 160ms ease, border-color 160ms ease, background 160ms ease,
    transform 160ms ease;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: rgba(126, 211, 255, 0.72);
    outline: none;
    color: #ffffff;
    background: rgba(21, 151, 229, 0.15);
    transform: translateX(4px);
  }
`;

export const ExplorerCard = styled.div`
  box-sizing: border-box;
  flex: 1;
  min-height: 206px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.055);

  strong {
    display: block;
    margin-bottom: 13px;
    color: #ffffff;
    font-size: 18px;
    line-height: 25px;
    font-weight: 540;
  }

  p {
    margin: 0;
    color: #b8c7ce;
    font-size: 14px;
    line-height: 23px;
  }
`;

export const ResponseCard = styled(ExplorerCard)`
  border-color: rgba(126, 211, 255, 0.58);
  background: #dff4ff;

  strong {
    color: #123449;
  }

  p {
    color: #486574;
  }
`;

export const Connector = styled.div`
  position: relative;
  min-height: 206px;

  &::before {
    content: "";
    position: absolute;
    top: calc(50% + 14px);
    right: 7px;
    left: 7px;
    border-top: 1px dashed rgba(183, 215, 229, 0.62);
  }

  &::after {
    content: "›";
    position: absolute;
    top: calc(50% - 1px);
    right: 4px;
    color: #9fc3d4;
    font-size: 24px;
  }

  @media (max-width: 850px) {
    min-height: 20px;

    &::before {
      top: 2px;
      right: auto;
      bottom: 2px;
      left: 50%;
      border-top: 0;
      border-left: 1px dashed rgba(183, 215, 229, 0.62);
    }

    &::after {
      content: "⌄";
      top: auto;
      right: auto;
      bottom: -5px;
      left: calc(50% - 6px);
      font-size: 18px;
    }
  }
`;

export const ExplorerSummary = styled.p`
  max-width: 760px;
  margin: 30px 0 0;
  color: #d3dee3;
  font-size: 15px;
  line-height: 24px;

  strong {
    color: #7ed3ff;
    font-weight: 550;
  }
`;

export const Walkthrough = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(52px, 7vw, 88px) max(6vw, 48px);
  background: #f2f7fa;
`;

export const WalkthroughInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

export const WalkthroughTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const WalkthroughTab = styled.button`
  flex: 0 0 auto;
  padding: 10px 15px;
  border: 1px solid #cbdbe4;
  border-radius: 999px;
  color: #607580;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  line-height: 19px;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: ${ink};
    outline: none;
    color: #ffffff;
    background: ${ink};
  }
`;

export const WalkthroughBody = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, 0.72fr) minmax(0, 1.28fr);
  gap: clamp(28px, 5vw, 66px);
  align-items: center;
  padding: clamp(26px, 4vw, 48px);
  border: 1px solid #dbe6ec;
  border-radius: 20px;
  background: #ffffff;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
    padding: 22px;
  }
`;

export const WalkthroughCopy = styled.div`
  h3 {
    margin: 0 0 13px;
    color: ${ink};
    font-size: clamp(26px, 3vw, 40px);
    line-height: 1.08;
    font-weight: 540;
    letter-spacing: -0.025em;
  }

  p {
    margin: 0 0 18px;
    color: #586d78;
    font-size: 15px;
    line-height: 24px;
  }
`;

export const WalkthroughMeta = styled.dl`
  margin: 0;

  div {
    padding: 11px 0;
    border-top: 1px solid #e3ebef;
  }

  dt,
  dd {
    margin: 0;
  }

  dt {
    margin-bottom: 3px;
    color: #8798a1;
    font-size: 10px;
    line-height: 15px;
    font-weight: 650;
    letter-spacing: 0.08em;
  }

  dd {
    color: #243e4b;
    font-size: 13px;
    line-height: 20px;
  }
`;

export const WalkthroughVisual = styled.figure`
  min-width: 0;
  margin: 0;

  button {
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    overflow: hidden;
    border: 0;
    border-radius: 14px;
    background: #edf3f6;
    cursor: zoom-in;
  }

  img {
    display: block;
    width: 100%;
    height: clamp(260px, 38vw, 500px);
    object-fit: contain;
    transition: transform 220ms ease;
  }

  button:hover img,
  button:focus-visible img {
    transform: scale(1.018);
  }

  figcaption {
    margin-top: 10px;
    color: #71838c;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
  }
`;

export const ZoomHint = styled.span`
  position: absolute;
  right: 12px;
  bottom: 12px;
  padding: 7px 10px;
  border-radius: 999px;
  color: #ffffff;
  background: rgba(15, 31, 39, 0.82);
  font-size: 11px;
  line-height: 16px;
`;

export const Lightbox = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 24px 24px;
  background: rgba(7, 13, 16, 0.94);

  img {
    display: block;
    max-width: min(1328px, 96vw);
    max-height: calc(100vh - 96px);
    border-radius: 12px;
    object-fit: contain;
  }
`;

export const LightboxClose = styled.button`
  position: absolute;
  top: 18px;
  right: 22px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 999px;
  color: #ffffff;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
`;

export const DecisionList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    margin-bottom: 13px;
    padding-left: 25px;
    text-align: left;
  }

  li::before {
    content: "↳";
    position: absolute;
    left: 0;
    color: ${blue};
    font-weight: 650;
  }
`;

export const Appendix = styled.details`
  box-sizing: border-box;
  width: 100%;
  padding: 0 max(6vw, 32px) clamp(48px, 7vw, 88px);
  background: #ffffff;

  html[data-theme="dark"] & {
    background: var(--page);
  }

  > summary {
    box-sizing: border-box;
    max-width: 1080px;
    margin: 0 auto;
    padding: 20px 24px;
    border: 1px solid #cfdee6;
    border-radius: 13px;
    cursor: pointer;
    list-style: none;
    color: #17313f;
    background: #ffffff;
    font-size: 14px;
    line-height: 21px;
    font-weight: 540;

    html[data-theme="dark"] & {
      border-color: #455258;
      color: var(--heading);
      background: var(--surface);
    }
  }

  > summary::-webkit-details-marker {
    display: none;
  }

  > summary::after {
    content: "+";
    float: right;
    color: #087bc1;
    font-size: 18px;
  }

  &[open] > summary::after {
    content: "×";
  }

  @media (max-width: 720px) {
    padding-right: 20px;
    padding-left: 20px;
  }
`;

export const AppendixGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  max-width: 1080px;
  margin: 18px auto 0;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const ProofCard = styled.button`
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  border: 1px solid #d5e2e8;
  border-radius: 12px;
  color: #263f4c;
  background: #ffffff;
  cursor: zoom-in;
  font: inherit;
  text-align: left;

  img {
    display: block;
    width: 100%;
    height: 180px;
    object-fit: cover;
    object-position: top;
  }

  strong,
  span {
    display: block;
    padding-right: 16px;
    padding-left: 16px;
  }

  strong {
    padding-top: 14px;
    font-size: 14px;
    line-height: 20px;
  }

  span {
    padding-top: 4px;
    padding-bottom: 16px;
    color: #71838c;
    font-size: 11px;
    line-height: 17px;
  }
`;

export const MetricBoard = styled.section`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(250px, 0.7fr) minmax(0, 1.3fr);
  gap: clamp(30px, 6vw, 88px);
  width: 100%;
  padding: clamp(58px, 8vw, 104px) max(7vw, 48px);
  color: #ffffff;
  background: #108bd3;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    padding: 50px 24px;
  }
`;

export const Metric = styled.div`
  span,
  strong {
    display: block;
  }

  span {
    margin-bottom: 12px;
    color: rgba(255, 255, 255, 0.76);
    font-size: 11px;
    line-height: 17px;
    font-weight: 650;
    letter-spacing: 0.08em;
  }

  strong {
    font-size: clamp(76px, 11vw, 150px);
    line-height: 0.86;
    font-weight: 550;
    letter-spacing: -0.07em;
  }
`;

export const MetricCopy = styled.div`
  align-self: end;

  h3 {
    max-width: 710px;
    margin: 0 0 18px;
    font-size: clamp(28px, 4vw, 46px);
    line-height: 1.1;
    font-weight: 520;
    letter-spacing: -0.025em;
  }

  p {
    max-width: 680px;
    margin: 0;
    color: rgba(255, 255, 255, 0.84);
    font-size: 14px;
    line-height: 23px;
  }
`;
